import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  Calendar,
  Award,
  GraduationCap,
  BookOpen,
  ShieldCheck,
  Building,
  User,
  Share2
} from 'lucide-react';

interface SharedCertificate {
  id: string;
  certificate_number: string;
  title: string;
  description: string;
  learning_outcomes: any;
  ects_credits: number;
  competency_points: number;
  eqf_level: number;
  assessment_result: {
    score: number;
    percentage?: number;
    passed: boolean;
  };
  course_completion_date: string;
  exam_date: string;
  issue_date: string;
  valid_until: string | null;
  sensor_name: string;
  status: 'active' | 'revoked' | 'expired';
  verification_code: string;
  candidate_name: string;
  member_name: string;
  association_name: string;
}

export function SharePage() {
  const { shareCode } = useParams<{ shareCode: string }>();
  const [certificate, setCertificate] = useState<SharedCertificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchShared();
  }, [shareCode]);

  async function fetchShared() {
    try {
      setLoading(true);
      const res = await fetch(`/api/certificates/share/${shareCode}`);

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Denne delingslenken er ugyldig eller deaktivert');
        }
        throw new Error('Kunne ikke hente delt attest');
      }

      const data = await res.json();
      setCertificate(data.certificate);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-kf-blue-100/50 to-white flex items-center justify-center p-4">
        <div className="card max-w-2xl w-full text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-kf-blue-600 mx-auto"></div>
          <p className="mt-6 text-kf-gray-600">Henter delt attest...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <div className="card max-w-2xl w-full text-center py-12">
          <XCircle className="w-20 h-20 text-red-500 mx-auto" />
          <h1 className="text-2xl font-bold text-kf-gray-900 mt-6">Deling ikke tilgjengelig</h1>
          <p className="text-kf-gray-600 mt-2">{error}</p>
          <p className="text-sm text-kf-gray-500 mt-4">
            Eieren kan ha deaktivert deling, eller lenken er feil.
          </p>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('no-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-kf-blue-100/30 to-white p-4">
      <div className="max-w-3xl mx-auto">
        {/* Shared Badge */}
        <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Share2 className="w-6 h-6 text-kf-blue-600" />
          <div>
            <p className="font-semibold text-kf-blue-900">Delt kompetanseattest</p>
            <p className="text-sm text-kf-blue-700">
              Denne attesten er delt av kandidaten. For offisiell verifisering,
              bruk den offisielle verifiseringslenken.
            </p>
          </div>
        </div>

        {/* Main Card */}
        <div className="card">
          {/* Status Banner */}
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-kf-gray-200">
            <CheckCircle className="w-10 h-10 text-kf-success-500" />
            <div>
              <h1 className="text-xl font-bold text-kf-gray-900">Gyldig kompetanseattest</h1>
              <p className="text-sm text-kf-gray-600">Attestnr: {certificate.certificate_number}</p>
            </div>
          </div>

          {/* Candidate */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-kf-gray-700 mb-2">
              <User className="w-5 h-5" />
              <span className="font-semibold">Kandidat</span>
            </div>
            <p className="text-lg font-bold text-kf-gray-900">{certificate.candidate_name}</p>
          </div>

          {/* Course Title */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-kf-gray-700 mb-2">
              <GraduationCap className="w-5 h-5" />
              <span className="font-semibold">Kurs</span>
            </div>
            <p className="text-lg font-bold text-kf-gray-900">{certificate.title}</p>
            {certificate.description && (
              <p className="text-kf-gray-600 mt-1">{certificate.description}</p>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-kf-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-kf-gray-600 text-sm mb-1">
                <Award className="w-4 h-4" />
                <span>ECTS</span>
              </div>
              <p className="font-bold text-kf-gray-900">{certificate.ects_credits}</p>
            </div>
            <div className="bg-kf-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-kf-gray-600 text-sm mb-1">
                <BookOpen className="w-4 h-4" />
                <span>EQF-nivå</span>
              </div>
              <p className="font-bold text-kf-gray-900">{certificate.eqf_level}</p>
            </div>
            <div className="bg-kf-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-kf-gray-600 text-sm mb-1">
                <Calendar className="w-4 h-4" />
                <span>Utstedt</span>
              </div>
              <p className="font-bold text-kf-gray-900 text-sm">{formatDate(certificate.issue_date)}</p>
            </div>
            <div className="bg-kf-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-kf-gray-600 text-sm mb-1">
                <ShieldCheck className="w-4 h-4" />
                <span>Resultat</span>
              </div>
              <p className="font-bold text-kf-success-600">
                {certificate.assessment_result?.percentage || certificate.assessment_result?.score || ''}%
              </p>
            </div>
          </div>

          {/* Issuer */}
          <div className="border-t border-kf-gray-200 pt-4">
            <div className="flex items-center gap-2 text-kf-gray-700 mb-2">
              <Building className="w-5 h-5" />
              <span className="font-semibold">Utstedt av</span>
            </div>
            <p className="text-kf-gray-900">{certificate.member_name}</p>
            <p className="text-sm text-kf-gray-600">{certificate.association_name}</p>
          </div>

          {/* Verification Notice */}
          <div className="mt-6 pt-4 border-t border-kf-gray-200">
            <p className="text-xs text-kf-gray-500 text-center">
              For offisiell verifisering av denne attesten, bruk QR-koden på attesten
              eller gå til www.kompetanseattest.no/verify/{certificate.verification_code}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
